/**
 * Identity + admin lookup for the current user. Backed by ``GET /api/me``,
 * which is the source of truth for ``is_admin`` (computed server-side from
 * ``ADMIN_EMAILS`` so the client can't claim admin without the backend
 * agreeing).
 *
 * Queried once via React Query and cached for the session — the answer
 * doesn't change while the JWT is valid, so we set a long ``staleTime``
 * to avoid refetching on every nav.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";

/** Per-tab access + nav visibility, resolved server-side from the admin config. */
export interface TabAccess {
  label: string;
  // Whether the user may reach the tab (drives route guards).
  accessible: boolean;
  // Whether it shows in the top nav (a tab can be accessible but hidden — direct
  // link only). Always ``false`` when not accessible.
  listed: boolean;
}

/** Search-chat caps for the user; ``null`` from /api/me means unlimited (admin). */
export interface UserLimits {
  session: number;
  hourly: number;
  daily: number;
}

export interface MeResponse {
  oid: string;
  preferred_username: string | null;
  name: string | null;
  roles: string[];
  is_admin: boolean;
  // Super-admin tier: may remove admins (see the Admin tab).
  is_owner: boolean;
  // Per-feature allowlists computed server-side (e.g. "portco_analysis").
  // Independent of ``is_admin`` — a feature can be rolled out per-person.
  features: string[];
  // Per-tab access + nav visibility, keyed by tab key (e.g. "analytics").
  tabs: Record<string, TabAccess>;
  // Search-chat caps for this user; ``null`` == unlimited (admin).
  limits: UserLimits | null;
}

export function useMe() {
  const apiCall = useApi();
  return useQuery<MeResponse, ApiError>({
    queryKey: ["me"],
    queryFn: ({ signal }) => apiCall<MeResponse>("/api/me", { signal }),
    // Tab access + usage limits are now live admin config (not JWT-bound), so
    // they can change mid-session. Keep a short freshness window and refetch on
    // focus/reconnect so an admin's change propagates without a full reload,
    // while still avoiding a refetch on every nav. An admin edit also
    // invalidates ["me"] directly for the editing user.
    staleTime: 60_000,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

/**
 * Convenience: ``true`` when the backend has confirmed the current user
 * is an admin. Returns ``false`` while the query is loading so the nav
 * starts in a "non-admin" state and progressively enhances when the
 * answer arrives — much better than flashing admin-only links to a
 * non-admin user.
 *
 * The ``use`` prefix is required so React's ``rules-of-hooks`` lint
 * recognizes this as a hook (it calls ``useMe`` internally). Call-site
 * reads cleanly as ``const isAdmin = useIsAdmin()``.
 */
export function useIsAdmin(): boolean {
  const { data } = useMe();
  return data?.is_admin === true;
}

/**
 * The current user's granted feature flags (``[]`` while loading), for
 * generic membership checks — e.g. filtering nav items by their optional
 * ``feature`` key without hard-coding each one.
 */
export function useFeatures(): string[] {
  const { data } = useMe();
  return data?.features ?? [];
}

/**
 * ``true`` when the backend has granted the named feature to the current
 * user. Same progressive-enhancement behavior as ``useIsAdmin``: ``false``
 * while loading so gated UI never flashes for an unauthorized user.
 */
export function useHasFeature(feature: string): boolean {
  const { data } = useMe();
  return data?.features?.includes(feature) === true;
}

/** Owner (super-admin) bit; ``false`` while loading. */
export function useIsOwner(): boolean {
  const { data } = useMe();
  return data?.is_owner === true;
}

/** Resolved per-tab access map (``{}`` while loading). */
export function useTabs(): Record<string, TabAccess> {
  const { data } = useMe();
  return data?.tabs ?? {};
}

/**
 * ``true`` when the backend confirms the user can reach ``tabKey``. ``false``
 * while loading so gated routes never flash for an unauthorized user.
 */
export function useCanAccessTab(tabKey: string): boolean {
  const { data } = useMe();
  return data?.tabs?.[tabKey]?.accessible === true;
}
